// ============================================================================
// L'IMPORT EN ARRIÈRE-PLAN — ce qui porte un import de fichier une fois la fenêtre fermée.
//
// UTILITÉ : la maquette ferme l'assistant au lancement et suit l'import dans une notification en bas à droite ;
// l'opérateur continue de travailler, même sur un autre écran. L'envoi ne peut donc pas vivre dans le composant de
// la fenêtre (il disparaît à sa fermeture) : il vit ici, et la notification comme l'écran des articles le LISENT.
//
// ⚠️ « EN COURS » EST LA VÉRITÉ D'UNE REQUÊTE EN VOL, PAS UN CHRONOMÈTRE : l'état ne passe à « terminé » qu'à la
//    réponse du serveur, avec SON compte rendu, et à « refusé » avec SON motif (§23.0). Aucun pourcentage n'est
//    inventé — le serveur écrit l'import d'un seul geste et ne publie aucune progression.
// ⚠️ UN SEUL IMPORT À LA FOIS : un second lancé pendant le premier écrirait dans la même base sans que l'opérateur
//    sache lequel le rapport décrit. L'assistant le refuse en le disant.
// ============================================================================
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { ApiError } from '@orgazone/api-envelope/client';
import { api, type ImportArticleRow, type ImportArticlesOptions, type ImportArticlesReport } from '../api';

/** Une ligne écartée par l'analyse de l'écran : elle n'est jamais partie, et le rapport la nomme quand même. */
export interface ImportScreenRejected { line: number; why: string; ex: string }

/** Ce qui identifie un import lancé — de quoi le dire, et de quoi le RENVOYER tel quel. */
interface ImportLaunch { key: string; fileName: string; rows: ImportArticleRow[]; options: ImportArticlesOptions; screenRejected: ImportScreenRejected[] }

/**
 * Un import lancé : en vol, terminé (avec ce que le serveur a fait), ou en échec (avec son motif réel).
 * ⚠️ UN ÉCHEC N'EST PAS TOUJOURS UN REFUS : `certain` dit que le magasin a RÉPONDU par un refus (rien n'a été écrit, une
 *    seule transaction) ; sans réponse (coupure, délai de la passerelle), l'import a PEUT-ÊTRE été écrit, et l'écran ne
 *    peut pas dire le contraire.
 */
export type ImportJob = { key: string; fileName: string; lines: number; screenRejected: ImportScreenRejected[] } & (
  | { state: 'running' }
  | { state: 'done'; report: ImportArticlesReport }
  | { state: 'failed'; reason: string; certain: boolean });

let current: ImportJob | null = null;
/** Le dernier import lancé — ce que « Réessayer » renvoie, sous la MÊME clé. */
let lastLaunch: ImportLaunch | null = null;
/** Combien de fois « Voir les articles » a été demandé — l'écran des articles se remet en tête à chaque demande. */
let showSeq = 0;
const listeners = new Set<() => void>();
const emit = (): void => { for (const l of listeners) l(); };
const subscribe = (l: () => void): (() => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** L'import en cours ou le dernier terminé — `null` quand la notification a été fermée. */
export function useImportJob(): ImportJob | null {
  return useSyncExternalStore(subscribe, () => current, () => current);
}

/** Un import est-il en vol ? — l'assistant refuse d'en lancer un second. */
export const importRunning = (): boolean => current?.state === 'running';

/**
 * LANCE l'import et rend la main tout de suite : la réponse arrive dans la notification.
 * ⚠️ LA CLÉ DE REJEU vient de l'assistant : un envoi rejoué après une coupure rend le même compte rendu, sans doubler.
 */
export function launchImport(p: ImportLaunch): void {
  if (importRunning()) return;
  lastLaunch = p;
  const base = { key: p.key, fileName: p.fileName, lines: p.rows.length, screenRejected: p.screenRejected };
  current = { ...base, state: 'running' };
  emit();
  api.importArticles(p.rows, p.options, p.key)
    .then((report) => { current = { ...base, state: 'done', report }; })
    // ⚠️ LE MOTIF RÉEL, ET CE QU'ON EN SAIT : un refus du magasin (4xx) n'a rien écrit ; sans réponse, on ne sait pas.
    .catch((e: unknown) => {
      const certain = e instanceof ApiError && e.status >= 400 && e.status < 500;
      current = { ...base, state: 'failed', reason: e instanceof Error ? e.message : String(e), certain };
    })
    .finally(emit);
}

/**
 * RÉESSAYER UN IMPORT RESTÉ SANS RÉPONSE — le même fichier, les mêmes choix, la MÊME clé.
 * ⚠️ C'EST LA CLÉ QUI REND LE GESTE SÛR : déjà écrit, le magasin rend le compte rendu de la première fois sans rien
 *    réécrire (`import_run`) ; une clé neuve doublerait les quantités d'un document existant.
 */
export function retryImport(): void {
  if (!lastLaunch || importRunning()) return;
  launchImport(lastLaunch);
}

/** Ferme la notification d'un import fini — un import en vol ne se ferme pas, il se suit. */
export function dismissImportJob(): void {
  if (current?.state === 'running') return;
  current = null;
  emit();
}

/**
 * « VOIR LES ARTICLES » : la notification se ferme, et l'écran des articles se remet en tête de sa liste (page 1, tri
 * par défaut — les articles écrits par l'import en premier), comme dans la maquette.
 * ⚠️ UN COMPTEUR, PAS UN DRAPEAU : l'import se lance depuis l'écran des articles, donc y naviguer ne change rien à
 *    l'écran — c'est la demande, relue par l'écran, qui le remet en tête même quand on y est déjà.
 */
export function requestShowImported(): void {
  showSeq += 1;
  current = null;
  emit();
}

/** Le nombre de demandes « Voir les articles » — l'écran des articles s'y abonne. */
export function useShowImportedSeq(): number {
  return useSyncExternalStore(subscribe, () => showSeq, () => showSeq);
}

/**
 * REMET UNE LISTE EN TÊTE quand un import se termine, et à chaque « Voir les articles » de sa notification — l'écran
 * des articles y passe sa propre remise (page 1, tri par défaut, relecture depuis le serveur), comme la maquette.
 * ⚠️ UNE FOIS PAR IMPORT (sa clé) ET PAR DEMANDE, pas à chaque rendu : la fonction passe par une référence, l'écran la
 *    recrée à chaque rendu et la mettre dans les dépendances relirait la liste sans fin.
 */
export function useAfterImport(onReset: () => void): void {
  const job = useImportJob();
  const termine = job?.state === 'done' ? job.key : null;
  const demandes = useShowImportedSeq();
  const latest = useRef(onReset);
  latest.current = onReset;
  // ⚠️ SEULS LES CHANGEMENTS APRÈS LE MONTAGE COMPTENT : l'écran qui s'ouvre lit déjà sa liste en tête ; rejouer à son
  //    montage un import terminé ou une demande d'hier la relisait une seconde fois pour rien.
  const atMount = useRef({ termine, demandes });
  useEffect(() => { if (termine && termine !== atMount.current.termine) latest.current(); }, [termine]);
  useEffect(() => { if (demandes > atMount.current.demandes) latest.current(); }, [demandes]);
}

/** Pour les bancs : repart d'un état vide entre deux cas. */
export function resetImportJobForTests(): void {
  current = null;
  lastLaunch = null;
  showSeq = 0;
  emit();
}
